import * as Yup from "yup";
import { ImageFileFormats, MimeEnum } from "../common/commonEnums";
import { IExtendedFile } from "../common/commonTypes";
import { settings } from "../settings";
import { accountService } from "../api/accountService";
import { musicService } from "../api/musicService";

export const validateUsername = async (
  value: string,
  currentUsername?: string
) => {
  try {
    await Yup.string()
      .required("form-error-username-required")
      .max(128, "form-error-username-max")
      .test("uniqueness", "username-uniqueness-error", async (value) => {
        if (!value || (currentUsername && value === currentUsername)) {
          return true;
        }

        try {
          return await accountService.fetchIsUsernameUnique(value);
        } catch (error) {
          return false;
        }
      })
      .validate(value);
    return undefined;
  } catch (error) {
    return error.message;
  }
};

export const validateTrackName = async (
  trackName: string,
  artistId: string
) => {
  try {
    await Yup.string()
      .max(64, "form-error-name-max")
      .required("form-error-name-required")
      .test(
        "uniqueness",
        "upload-music-track-name-uniqueness",
        async (value) => {
          if (!value) {
            return true;
          }

          try {
            return await musicService.fetchIsTrackNameUnique(
              trackName,
              artistId
            );
          } catch (error) {
            return false;
          }
        }
      )
      .validate(trackName);
    return undefined;
  } catch (error) {
    return error.message;
  }
};

export const validateProfileUrl = async (
  value: string,
  currentUrl?: string
) => {
  try {
    await Yup.string()
      .required("profile-url-required")
      .min(3, "profile-url-min")
      .max(128, "profile-url-max")
      .matches(/^[A-Za-z0-9_]+$/, "profile-url-chars")
      .test("uniqueness", "profile-url-uniqueness-error", async (value) => {
        if (!value || (currentUrl && value === currentUrl)) {
          return true;
        }

        try {
          return await accountService.fetchIsUserUriUnique(value);
        } catch (error) {
          return false;
        }
      })
      .validate(value);
    return undefined;
  } catch (error) {
    return error.message;
  }
};

export const validateExtendedFileType =
  (validFileTypes: string[]) => (extendedFile: IExtendedFile) => {
    if (!extendedFile) {
      return true;
    }

    return validFileTypes.includes(extendedFile.file.type);
  };

export const validateExtendedFileSize =
  (maxSize: number) => (extendedFile: IExtendedFile) => {
    if (!extendedFile) {
      return true;
    }

    return extendedFile.file.size <= maxSize;
  };

export const validateImageDimensions =
  (minWidth: number, minHeight: number) =>
  (extendedFile: IExtendedFile): boolean => {
    if (
      !extendedFile ||
      !extendedFile.meta.width ||
      !extendedFile.meta.height
    ) {
      return true;
    }

    return (
      extendedFile.meta.width >= minWidth &&
      extendedFile.meta.height >= minHeight
    );
  };

export const getImageValidationSchemaPart = (
  minWidth: number,
  minHeight: number,
  maxSize: number = settings.MAX_IMAGE_SIZE,
  imageFormats: MimeEnum[] = ImageFileFormats,
  formatError = "form-error-not-image"
) =>
  Yup.mixed()
    .test("format", formatError, validateExtendedFileType(imageFormats))
    .test("size", "form-error-file-to-big", validateExtendedFileSize(maxSize))
    .test(
      "dimensions",
      "form-error-image-dimensions-not-met",
      validateImageDimensions(minWidth, minHeight)
    );
